import { scrollTargetIntoView } from "@/guides/runSteps";
import type { GuideStep, ViewGuide } from "@/guides/types";
import { ResponsiveModal } from "@c/shared";
import { cn } from "@u/cn";
import { buildRoute } from "@u/routes";
import Button from "@ui/button";
import { buttonVariants } from "@ui/button-variants";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui/tooltip";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { BookOpen, ChartColumn, HelpCircle } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

/**
 * Reusable per-view user guide. Renders a `?` button (with a first-visit dot)
 * that opens a help panel; the panel can launch an interactive spotlight tour
 * (driver.js) over elements the view marks with `data-guide="…"`.
 *
 * Wire it via `DashboardPage`'s `guide` prop — pass a `ViewGuide` from
 * `src/guides/<view>.ts`. Additive: views without a guide render nothing.
 */
export function DashboardGuide({
  guide,
  compact,
  iconOnly,
  variant = "default",
  nudge = false,
  openSignal,
  onOpenChange,
}: {
  guide: ViewGuide;
  /** Inline trigger (icon + label, no border) for use inside a row/card. */
  compact?: boolean;
  /** Drop the labels even in `compact` — icon-only (tooltip keeps the name). */
  iconOnly?: boolean;
  /** `glass` = frosted HUD trigger, legible over any photo (public viewer). */
  variant?: "default" | "glass";
  /** First-visit coachmark: a small bubble beside the `?` inviting the visitor
   *  to open the guide. Shows once (same `seen` key), auto-dismisses after a
   *  few seconds or on the first pointer-down anywhere (drag/tap on the dome). */
  nudge?: boolean;
  /** Abre el panel desde afuera (control remoto del viewer). El host es dueño
   *  del cierre: se le avisa por `onOpenChange`. */
  openSignal?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const helpLabel = t("guide_help", "Ayuda");
  const manualLabel = t("guide_full_manual", "Manual completo");
  const analyticsLabel = t("guide_view_analytics", "Ver estadísticas");
  // Help / manual / analytics share one trigger shape — icon-only pill outside a
  // row, icon+label inline (`compact`), tooltip always carries the name.
  const triggerCls =
    variant === "glass"
      ? buttonVariants({ design: "glass", size: "icon" })
      : cn(
          "inline-flex items-center justify-center text-muted-foreground transition-colors hover:text-foreground",
          compact
            ? "h-7 gap-1 rounded-lg px-2 text-xs"
            : "h-9 w-9 rounded-xl border border-border/60",
        );
  const iconSize = compact ? 13 : 16;
  // Glass = public viewer HUD, pinned to the right rail → tooltip opens left so
  // it doesn't overflow the edge. Dashboard (default) keeps dropping below.
  const tipSide = variant === "glass" ? "left" : "bottom";
  const [openState, setOpenState] = useState(false);
  const open = openSignal ?? openState;
  const setOpen = (v: boolean) => {
    setOpenState(v);
    onOpenChange?.(v);
  };
  const seenKey = `guide_seen_${guide.key}`;
  const [seen, setSeen] = useState(() => {
    try {
      return !!localStorage.getItem(seenKey);
    } catch {
      return true;
    }
  });

  const [nudgeOpen, setNudgeOpen] = useState(false);
  useEffect(() => {
    if (!nudge || seen) return;
    // Wait for the intro pan/cinematic to settle before nudging.
    const show = window.setTimeout(() => setNudgeOpen(true), 2500);
    return () => window.clearTimeout(show);
  }, [nudge, seen]);
  useEffect(() => {
    if (!nudgeOpen) return;
    const hide = () => setNudgeOpen(false);
    const t = window.setTimeout(hide, 7000);
    window.addEventListener("pointerdown", hide, { once: true });
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("pointerdown", hide);
    };
  }, [nudgeOpen]);

  const markSeen = () => {
    try {
      localStorage.setItem(seenKey, "1");
    } catch {
      /* ignore storage errors */
    }
    setSeen(true);
  };

  const startTour = (startKey?: string) => {
    setOpen(false);
    guide.setup?.();
    // Skip steps whose target is absent or collapsed (e.g. `hidden sm:flex`
    // filters on mobile) — driver.js otherwise spotlights a zero-size rect.
    // No target = centered popover, always kept.
    const isVisible = (sel?: string) => {
      if (!sel) return true;
      const el = document.querySelector(sel);
      if (!el) return false;
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    };
    // Build + drive only AFTER the help panel is gone from the DOM. On mobile
    // the drawer (vaul) overlays and scroll-locks the page while it animates
    // closed; measuring targets before that races to zero-size rects (every step
    // filtered out → drive() does nothing) and can't scroll below the fold.
    const panelPresent = () =>
      !!document.querySelector(
        '[data-slot="drawer-content"], [data-slot="dialog-content"]',
      );
    let frames = 0;
    const driveWhenReady = () => {
      if (panelPresent() && frames <= 90) {
        frames += 1;
        requestAnimationFrame(driveWhenReady);
        return;
      }
      const mapStep = (s: GuideStep) => {
        const dyn = s.dynamic?.() ?? {};
        return {
          element: s.target,
          popover: {
            title: t(dyn.titleKey ?? s.titleKey),
            description: t(dyn.bodyKey ?? s.bodyKey),
          },
        };
      };

      // Visible steps, carrying their sub-guide + source key alongside the
      // driver payload (the key lets the list jump straight to a step).
      const plan = guide.steps
        .filter((s) => isVisible(s.target))
        .map((s) => ({ ...mapStep(s), sub: s.subGuide, key: s.titleKey }));

      // Every control this guide points at is absent right now (empty state,
      // plan-gated, or a sub with no data yet). Rather than a dead no-op, show a
      // single centered popover with the section overview so the tour still says
      // something useful.
      if (!plan.length) {
        plan.push({
          element: undefined,
          popover: { title: t(guide.titleKey), description: t(guide.introKey) },
          sub: undefined,
          key: guide.titleKey,
        });
      }

      // One active driver at a time (main OR a nested sub-tour). Shared so
      // onHighlighted can refresh whichever is driving.
      const active: { current: ReturnType<typeof driver> | null } = {
        current: null,
      };
      const commonOpts = {
        showProgress: true,
        popoverClass: "andar-guide",
        nextBtnText: t("guide_next", "Siguiente"),
        prevBtnText: t("guide_prev", "Atrás"),
        doneBtnText: t("guide_done", "Listo"),
        // Center the target and reposition the popover once the scroll settles.
        // Without this driver.js lays out the popover against the pre-scroll
        // rect, so low targets get covered until the user nudges the page.
        // (No-op embebido: ver scrollTargetIntoView.)
        onHighlighted: (el: Element | undefined) => {
          if (!el) return;
          scrollTargetIntoView(el);
          requestAnimationFrame(() => active.current?.refresh());
        },
      };

      const resumeMain = (idx: number) => {
        if (idx >= plan.length) return;
        requestAnimationFrame(() => {
          active.current = makeMain();
          active.current.drive(idx);
        });
      };

      // Open the modal, wait for its contents, run the nested tour, then resume.
      const openSub = (
        sub: NonNullable<GuideStep["subGuide"]>,
        mainSel: string | undefined,
        resumeIdx: number,
      ) => {
        if (sub.open) sub.open();
        else if (mainSel)
          (document.querySelector(mainSel) as HTMLElement | null)?.click();
        let tries = 0;
        const go = () => {
          const visible = sub.steps.filter((s) => isVisible(s.target));
          if (!visible.length && tries < 90) {
            tries += 1;
            requestAnimationFrame(go);
            return;
          }
          if (!visible.length) return resumeMain(resumeIdx);
          // While the sub-tour runs, swallow pointerdowns that land outside the
          // guide popover — otherwise a stray click hits the modal's backdrop
          // and Radix dismisses it mid-tour. Capture phase runs before Radix's
          // bubble-phase dismiss listener. Popover buttons stay clickable.
          const guard = (e: Event) => {
            const el = e.target as Element | null;
            if (el?.closest?.(".driver-popover")) return;
            e.stopImmediatePropagation();
          };
          document.addEventListener("pointerdown", guard, true);
          const subd = driver({
            ...commonOpts,
            steps: visible.map(mapStep),
            onDestroyed: () => {
              document.removeEventListener("pointerdown", guard, true);
              if (sub.close) sub.close();
              else
                document.dispatchEvent(
                  new KeyboardEvent("keydown", { key: "Escape", bubbles: true }),
                );
              resumeMain(resumeIdx);
            },
          });
          active.current = subd;
          subd.drive();
        };
        requestAnimationFrame(go);
      };

      const makeMain = () =>
        driver({
          ...commonOpts,
          steps: plan.map((p) => ({ element: p.element, popover: p.popover })),
          // Inject a "Show more" button on steps that carry a sub-guide.
          onPopoverRender: (
            popover: { footerButtons: HTMLElement },
            opts: { state: { activeIndex?: number } },
          ) => {
            const idx = opts.state.activeIndex ?? 0;
            const p = plan[idx];
            if (!p?.sub) return;
            // Neutral class ONLY — driver.js delegates clicks by matching its
            // own `driver-popover-*-btn` classes, so reusing them would make it
            // hijack this click as "next" and our handler would never open the
            // modal. Style inline to match the popover buttons instead.
            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "andar-guide-more";
            btn.textContent = t("guide_show_more", "Mostrar más");
            btn.style.cssText =
              "cursor:pointer;font-size:13px;padding:3px 10px;border-radius:5px;" +
              "background:var(--color-primary,#0c3059);color:#fff;border:none;margin-left:4px;";
            btn.addEventListener("click", (e) => {
              e.preventDefault();
              e.stopPropagation();
              active.current?.destroy();
              openSub(p.sub!, p.element, idx + 1);
            });
            popover.footerButtons.appendChild(btn);
          },
        });

      const startIdx = startKey
        ? Math.max(
            0,
            plan.findIndex((p) => p.key === startKey),
          )
        : 0;
      active.current = makeMain();
      active.current.drive(startIdx);
    };
    requestAnimationFrame(driveWhenReady);
  };

  return (
    <>
      <span className="inline-flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => {
                setOpen(true);
                markSeen();
              }}
              aria-label={helpLabel}
              className={cn("relative", triggerCls)}
            >
              <HelpCircle size={iconSize} />
              {compact && !iconOnly && <span>{helpLabel}</span>}
              {!seen && (
                <span className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-primary" />
              )}
              <AnimatePresence>
                {nudgeOpen && !seen && (
                  <motion.span
                    initial={{ opacity: 0, x: 6 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 6 }}
                    transition={{ duration: 0.25 }}
                    role="status"
                    className="pointer-events-none absolute right-full top-1/2 mr-2 -translate-y-1/2 whitespace-nowrap rounded-lg border border-border/60 bg-popover px-3 py-1.5 text-xs font-medium text-popover-foreground shadow-md"
                  >
                    {t("guide_first_visit_nudge", "¿Primera vez? Tocá acá para ver cómo moverte")}
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
          </TooltipTrigger>
          <TooltipContent side={tipSide}>{helpLabel}</TooltipContent>
        </Tooltip>

        {guide.manualTopic && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to={buildRoute.help(guide.manualTopic, guide.manualAnchor)}
                aria-label={manualLabel}
                className={triggerCls}
              >
                <BookOpen size={iconSize} />
                {compact && !iconOnly && <span>{manualLabel}</span>}
              </Link>
            </TooltipTrigger>
            <TooltipContent side={tipSide}>{manualLabel}</TooltipContent>
          </Tooltip>
        )}

        {guide.analyticsFocus && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to={buildRoute.analytics(guide.analyticsFocus)}
                aria-label={analyticsLabel}
                className={triggerCls}
              >
                <ChartColumn size={iconSize} />
                {compact && !iconOnly && <span>{analyticsLabel}</span>}
              </Link>
            </TooltipTrigger>
            <TooltipContent side={tipSide}>{analyticsLabel}</TooltipContent>
          </Tooltip>
        )}
      </span>

      <ResponsiveModal
        open={open}
        onOpenChange={setOpen}
        title={t(guide.titleKey)}
        description={t(guide.introKey)}
        size="md"
        footer={
          <Button
            label={t("guide_start_tour", "Hacer el tour")}
            onClick={() => startTour()}
            className="w-full"
          />
        }
      >
        <div className="space-y-1 pt-2">
          {guide.steps.map((s, i) => (
            <button
              key={s.titleKey}
              type="button"
              onClick={() => startTour(s.titleKey)}
              className="flex w-full gap-3 rounded-lg p-2 text-left transition-colors hover:bg-muted/50"
            >
              <span
                className={cn(
                  "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full",
                  "bg-primary/10 text-primary text-xs font-medium tabular-nums",
                )}
              >
                {i + 1}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium">{t(s.titleKey)}</p>
                <p className="text-xs text-muted-foreground leading-snug">
                  {t(s.bodyKey)}
                </p>
              </div>
            </button>
          ))}
        </div>
      </ResponsiveModal>
    </>
  );
}
